import { useMutation, useQueryClient } from '@tanstack/react-query';
import { jobPositionService } from '../services/jobPosition.service';
import type {
  AddCandidateToJobPositionRequest,
  JobPositionCandidateWithDetails,
} from '../types/jobPositionCandidate.types';
import { JobPositionId } from '@/shared/types/branded.types';

interface UseAddCandidateToPositionReturn {
  addCandidate: (
    params: { positionId: string; data: AddCandidateToJobPositionRequest },
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => void;
  addCandidateAsync: (params: {
    positionId: string;
    data: AddCandidateToJobPositionRequest;
  }) => Promise<JobPositionCandidateWithDetails>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
}

export function useAddCandidateToPosition(): UseAddCandidateToPositionReturn {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      positionId,
      data,
    }: {
      positionId: string;
      data: AddCandidateToJobPositionRequest;
    }) => jobPositionService.addCandidate({ positionId: JobPositionId(positionId) }, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['job-position-candidates', variables.positionId],
      });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
    },
  });

  return {
    addCandidate: mutation.mutate,
    addCandidateAsync: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error,
  };
}
